/*
 * komplettrad.js — the client half of the Komplettrad flow (wheel + tyre set).
 * Pure vanilla ES6, dependency-free, loaded with `defer` by the sections.
 *
 * Two custom elements, both progressive enhancement only:
 *
 * <komplettrad-cta> (sections/wheel-tyre-link.liquid, product page):
 *   Without JS the button is a PLAIN LINK to the tyre target of the card
 *   (computed ONCE in wheel-tyre-link.liquid: PKW tag route, LKW/Agrar
 *   collection) — no broken form. With JS the click becomes: add 4x the
 *   current variant via window.ApCartAdd (assets/automotive.js — the ONE
 *   evaluation of cart adds), stamp the cart attributes
 *   `komplettrad_zoll` = wheel.diameter, `komplettrad_variant` = the
 *   variant id and `komplettrad_ziel` = that very link via /cart/update.js,
 *   then follow the link. On ANY rejection
 *   (also a partial add: Shopify answers 422 but keeps the available units)
 *   nothing is stamped and the customer stays on the page with a message
 *   that names the real quantity in the cart. The attributes — NOT a
 *   metafield — carry the state, because the collection page that reads them
 *   is page-cached and its Liquid never sees the visitor's cart. The button
 *   itself only renders with verifiable stock >= 4 (wheel-tyre-link.liquid).
 *
 * <komplettrad-banner> (sections/komplettrad-banner.liquid, collection page):
 *   Server-side the section is an always-empty hidden shell (page cache!).
 *   On connect we read /cart.js; only when the cart carries komplettrad_zoll
 *   AND komplettrad_variant do we fill the copy (data-template attributes
 *   with [[zoll]] and [[menge]] tokens, textContent only — cart attributes are
 *   visitor-settable via URL, so the values are validated AND never parsed as
 *   HTML) and reveal the bar. [[menge]] is the REAL quantity of that variant
 *   in the cart right now — not a promised "4". The dismiss X clears the
 *   attributes and hides the bar again.
 *
 * The small pub/sub bridge is duplicated from needed-with.js on purpose:
 * automotive.js is orchestrator-owned and its helpers are private to its
 * IIFE. The ONE exception is window.ApCartAdd, which automotive.js exposes on
 * purpose (every page loads it from layout/theme.liquid): a second copy of the
 * cart-add evaluation is exactly how the dead `response.status` check
 * survived here. Without it the button degrades to its plain link.
 */
(function () {
  'use strict';

  /* Pub/Sub bridge — Dawn's publish/PUB_SUB_EVENTS are plain (non-window)
   * globals; resolve them lazily and never throw when they are absent. */
  function getPublish() {
    if (typeof window.publish === 'function') return window.publish;
    if (typeof publish === 'function') return publish; // eslint-disable-line no-undef
    return null;
  }
  function cartUpdateEventName() {
    try {
      // eslint-disable-next-line no-undef
      if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        // eslint-disable-next-line no-undef
        return PUB_SUB_EVENTS.cartUpdate;
      }
    } catch (e) {
      /* ReferenceError when the constant is missing — fall through. */
    }
    return 'cart-update';
  }

  var JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  /* Cart attributes are settable by anyone via /cart/update?attributes[...]=,
   * so komplettrad_zoll is untrusted input: accept only a plain inch number
   * (optionally decimal, e.g. "17" or "17.5"/"17,5") before it goes anywhere
   * near a URL or the page. */
  var ZOLL_RE = /^[0-9]{1,2}(?:[.,][0-9]{1,2})?$/;
  // komplettrad_variant: dieselbe Vorsicht — nur eine nackte Varianten-Id.
  var VARIANT_RE = /^[0-9]{1,20}$/;
  // komplettrad_ziel: der Link des Knopfs (wheel-tyre-link.liquid rechnet das
  // Reifen-Ziel EINMAL). Nur ein relativer Kollektionspfad dieses Shops, optional
  // mit Sprachpraefix und EINEM Tag-Segment — nie eine fremde Adresse (das Attribut
  // ist per URL setzbar), nie Query oder Fragment.
  var ZIEL_RE = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[a-z0-9][a-z0-9-]{0,99}(?:\/[a-z0-9][a-z0-9-]{0,99})?$/;

  // Das Ziel des Hinweis-Links ist komplettrad_ziel — die Adresse, die
  // wheel-tyre-link.liquid fuer die Reifen-Karte gerechnet hat (Sofortkorrektur
  // E15, 26.09.2026: PKW-Tag-Route bzw. LKW-/Agrar-Kollektion, nie die Zoll-
  // Kollektion mit Motorradreifen). Diese Funktion ist NUR der Rueckfall fuer
  // Warenkoerbe, die der Knopf vor dem 26.09.2026 gestempelt hat (ohne
  // komplettrad_ziel):
  //   ganzer Zoll   "17"   -> "/collections/pkw-transporterreifen/17-zoll"
  //   halber Zoll   "22.5" -> "/collections/22-5-zoll" (altes Ziel; Shopify
  //                           schreibt "." als "-")
  function tyreCollectionUrl(zoll) {
    var z = String(zoll).replace(',', '.');
    var root = (window.routes && window.routes.root_url) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    if (/^[0-9]+$/.test(z)) return root + 'collections/pkw-transporterreifen/' + z + '-zoll';
    return root + 'collections/' + z.replace('.', '-') + '-zoll';
  }

  // Fill an element whose copy travels as data-template="... [[zoll]] ..."
  // (the inch value and the cart quantity are only known client-side).
  // textContent only — XSS-safe.
  function fillTemplate(el, zoll, menge) {
    if (!el) return;
    var template = el.getAttribute('data-template') || '';
    el.textContent = template
      .split('[[zoll]]')
      .join(zoll)
      .split('[[menge]]')
      .join(String(menge == null ? '' : menge));
  }

  // Stueck dieser Variante im Warenkorb (Summe ueber alle Zeilen).
  function mengeImKorb(cart, variantId) {
    var id = String(variantId);
    var items = (cart && cart.items) || [];
    var summe = 0;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it) continue;
      var vid = it.variant_id != null ? it.variant_id : it.id;
      if (String(vid) === id) summe += Number(it.quantity) || 0;
    }
    return summe;
  }

  var LEERE_ATTRIBUTE = { komplettrad_zoll: '', komplettrad_variant: '', komplettrad_ziel: '' };

  // Hinweis-Ziel: das gestempelte komplettrad_ziel, sonst der Rueckfall.
  function hinweisZiel(ziel, zoll) {
    if (ziel && ZIEL_RE.test(String(ziel))) return String(ziel);
    return tyreCollectionUrl(zoll);
  }

  /* ----------------------------------------------------------------------
   * <komplettrad-cta>
   * Markup contract (sections/wheel-tyre-link.liquid):
   *   host: data-zoll (wheel.diameter as string), data-variant-id (fallback),
   *         data-msg-partial ("… [n] …", Teilmenge im Korb), data-msg-error
   *   link: [data-komplettrad-go] (href = the tyre target, the no-JS path —
   *         stamped as komplettrad_ziel when it passes ZIEL_RE)
   *   error: [data-komplettrad-error] (server-rendered [hidden], role=alert)
   * -------------------------------------------------------------------- */
  class KomplettradCta extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.link = this.querySelector('[data-komplettrad-go]');
      this.errorEl = this.querySelector('[data-komplettrad-error]');
      if (!this.link || typeof fetch !== 'function') return; // Stay a plain link.
      this.link.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.link) this.link.removeEventListener('click', this.onClick);
    }

    // The variant to add: prefer the live id from the main product form (it
    // changes on variant switch), fall back to the server-rendered id — same
    // resolution as needed-with.js.
    variantId() {
      // ⚠ NUR IM HAUPTINHALT SUCHEN. `document.querySelector` trifft in
      // BAUMREIHENFOLGE, und der Warenkorb-Schubkasten steht in
      // layout/theme.liquid:557 VOR <main> (562). Bei nicht leerem Korb
      // rendert der Montagecheck dort eigene Kaufformulare
      // (snippets/ap-cart-part.liquid:81). Dokumentweit gewann also das
      // Partnerformular, und der Komplettrad-Knopf legte viermal den
      // Zentrierring statt der Felge ab — der servergerenderte richtige Wert
      // in data-variant-id ist nur der Rueckfall und wurde verdraengt.
      var wurzel = document.getElementById('MainContent') || document;
      var form =
        wurzel.querySelector('product-form form[action*="/cart/add"]') ||
        wurzel.querySelector('form[action*="/cart/add"]');
      var mainId = form && form.querySelector('[name="id"]');
      if (mainId && mainId.value) return mainId.value;
      return this.dataset.variantId || '';
    }

    onClick(event) {
      event.preventDefault();
      if (this.busy) return;

      var self = this;
      var target = this.link.getAttribute('href');
      var variantId = this.variantId();
      var cartAdd = window.ApCartAdd;
      if (!variantId || !cartAdd) {
        // Nothing to add (or the shared evaluation in automotive.js is
        // missing) — degrade to the no-JS behaviour: just navigate.
        window.location.assign(target);
        return;
      }

      var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';

      this.setBusy(true);
      this.showError('');

      // EINE Auswertung fuer jedes Hinzufuegen (window.ApCartAdd,
      // assets/automotive.js). Vorher stand hier `response.status >= 400` im
      // JSON-Koerper — auf /cart/add (window.routes.cart_add_url, ohne .js)
      // antwortet Shopify bei zu wenig Bestand aber mit HTTP 422 und NUR
      // {"errors": "Aufgrund der Verfuegbarkeit wurden nur 2 Artikel …"} und
      // legt die 2 trotzdem ab. Die Pruefung griff nie: der Knopf leitete
      // weiter, und die Kollektion meldete "Ihre 4 Felgen sind im Warenkorb"
      // ueber 2 Felgen (live, Mak Stone 6, Bestand 2). Jetzt: bei jeder
      // Ablehnung bleibt der Kunde hier, das Attribut wird NICHT gesetzt, und
      // die Meldung nennt die echte Menge im Korb (Zaehler neu gezeichnet).
      // Satzgroesse der Vorlage (Pruefrunde 2 Phase B): LKW-Felge 2, sonst 4 — dieselbe Zahl wie
      // Knopftext und Kaufkarte (sections/wheel-tyre-link.liquid, data-satz).
      var satz = this.satz();
      cartAdd
        .add([{ id: Number(variantId) || variantId, quantity: satz }], { texte: this.texte() })
        .then(function (ergebnis) {
          // Teilmenge, die den Satz trotzdem voll macht: lagen schon Felgen
          // dieser Variante im Korb, lehnt Shopify die 4 ab (422), fuellt aber
          // bis zum Bestand auf — danach liegen >= 4 drin (Vorschau 25.09.2026,
          // Oxxo Oberon: 2 vorab, Klick -> 422, 4 im Korb). Das ist ein
          // kompletter Radsatz: Attribut setzen und weiter wie beim Erfolg,
          // statt "Nicht alle 4 … dort liegen jetzt 4" zu melden. Kopfzaehler
          // und Schublade hat ApCartAdd.abgleich schon neu gezeichnet.
          // Seit 26.09.2026 unterscheidet ApCartAdd "Teilmenge abgelegt"
          // ('teil') von "nichts dazu, der Korb hatte schon alles" ('voll') —
          // fuer den Satz zaehlt beides gleich: liegen >= 4 drin, ist er komplett.
          var imKorbGenug = (ergebnis.status === 'teil' || ergebnis.status === 'voll') && ergebnis.imKorb >= satz;
          var satzVoll = imKorbGenug;
          if (ergebnis.status !== 'ok' && !satzVoll) {
            self.setBusy(false);
            self.showError(ergebnis.meldung);
            return null;
          }
          var pub = getPublish();
          if (pub && ergebnis.status === 'ok') {
            pub(cartUpdateEventName(), { source: 'komplettrad-cta', cartData: ergebnis.data });
          }
          // Stamp the attributes the collection banner reads: the inch size
          // and the VARIANT — the banner counts that variant's real quantity
          // in the cart instead of promising "4". If ONLY this step fails the
          // wheels are already in the cart — still navigate, the customer
          // merely misses the reminder banner.
          return fetch(updateUrl, {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify({
              attributes: {
                komplettrad_zoll: self.dataset.zoll || '',
                komplettrad_variant: String(variantId),
                komplettrad_ziel: ZIEL_RE.test(String(target || '')) ? String(target) : '',
              },
            }),
          })
            .catch(function (err) {
              console.warn('Komplettrad attribute update failed:', err);
            })
            .then(function () {
              window.location.assign(target);
            });
        });
    }

    satz() {
      var n = parseInt(this.getAttribute('data-satz') || '', 10);
      return n === 2 ? 2 : 4;
    }

    texte() {
      return {
        teilmenge: this.getAttribute('data-msg-partial') || '',
        fehler: this.getAttribute('data-msg-error') || '',
      };
    }

    // Die Fehlerzeile ist server-seitig [hidden] und traegt den allgemeinen
    // Fehlertext; eine konkrete Meldung (Teilmenge, Shopify-Grund) ersetzt ihn.
    showError(text) {
      if (!this.errorEl) return;
      if (!text) {
        this.errorEl.setAttribute('hidden', '');
        return;
      }
      this.errorEl.textContent = text;
      this.errorEl.removeAttribute('hidden');
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      if (!this.link) return;
      // Sichtbarer Ladezustand wie an jedem anderen Kaufknopf (Dawns Spinner,
      // window.ApCartAdd.laedt) — aria-busy allein dimmte den Knopf nur.
      var cartAdd = window.ApCartAdd;
      if (cartAdd && typeof cartAdd.laedt === 'function') cartAdd.laedt(this.link, isBusy);
      if (isBusy) {
        this.link.setAttribute('aria-busy', 'true');
        this.link.classList.add('is-loading');
      } else {
        this.link.removeAttribute('aria-busy');
        this.link.classList.remove('is-loading');
      }
    }
  }

  /* ----------------------------------------------------------------------
   * <komplettrad-banner>
   * Markup contract (sections/komplettrad-banner.liquid):
   *   host: [hidden] server-side, ALWAYS — reveal is exclusively client-side.
   *   text: [data-komplettrad-text] with data-template
   *   link: [data-komplettrad-link] with data-template (href set here)
   *   dismiss: [data-komplettrad-dismiss]
   * -------------------------------------------------------------------- */
  class KomplettradBanner extends HTMLElement {
    constructor() {
      super();
      this.onDismiss = this.onDismiss.bind(this);
    }

    connectedCallback() {
      if (typeof fetch !== 'function') return; // Stays hidden — correct no-JS state.
      this.textEl = this.querySelector('[data-komplettrad-text]');
      this.linkEl = this.querySelector('[data-komplettrad-link]');
      this.dismissBtn = this.querySelector('[data-komplettrad-dismiss]');
      if (this.dismissBtn) this.dismissBtn.addEventListener('click', this.onDismiss);

      var self = this;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          var attrs = (cart && cart.attributes) || {};
          var zoll = attrs.komplettrad_zoll;
          var variante = attrs.komplettrad_variant;
          if (!zoll || !ZOLL_RE.test(String(zoll))) return;
          // Ohne Varianten-Attribut (Warenkorb aus der Zeit vor dem 24.09.2026)
          // kennen wir die Felge nicht und koennten nur "4" behaupten — genau
          // die Zusage, die sich als falsch erwiesen hat. Dann kein Hinweis.
          if (!variante || !VARIANT_RE.test(String(variante))) return;
          // Ehrlichkeits-Guard: die Zahl im Hinweis ist die ECHTE Menge dieser
          // Felge im Warenkorb. Hat der Kunde sie wieder entfernt, den Hinweis
          // unterdruecken UND die verwaisten Attribute gleich mitloeschen.
          var menge = mengeImKorb(cart, variante);
          if (!cart.item_count || menge < 1) {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ attributes: LEERE_ATTRIBUTE }),
            }).catch(function () {});
            return;
          }
          self.hydrate(String(zoll), menge, attrs.komplettrad_ziel);
        })
        .catch(function () {
          /* Network hiccup: the banner simply stays hidden. */
        });
    }

    disconnectedCallback() {
      if (this.dismissBtn) this.dismissBtn.removeEventListener('click', this.onDismiss);
    }

    hydrate(zoll, menge, ziel) {
      fillTemplate(this.textEl, zoll, menge);
      fillTemplate(this.linkEl, zoll, menge);
      if (this.linkEl) this.linkEl.setAttribute('href', hinweisZiel(ziel, zoll));
      this.removeAttribute('hidden');
    }

    onDismiss() {
      // Hide immediately (perceived speed), then clear the attribute so the
      // banner does not come back on the next collection page.
      this.setAttribute('hidden', '');
      var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
      fetch(updateUrl, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ attributes: LEERE_ATTRIBUTE }),
      }).catch(function (err) {
        console.warn('Komplettrad dismiss failed:', err);
      });
    }
  }

  function defineComponents() {
    if (!('customElements' in window)) return;
    if (!customElements.get('komplettrad-cta')) {
      customElements.define('komplettrad-cta', KomplettradCta);
    }
    if (!customElements.get('komplettrad-banner')) {
      customElements.define('komplettrad-banner', KomplettradBanner);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', defineComponents, { once: true });
  } else {
    defineComponents();
  }
})();
